import { useState, useEffect, useCallback } from "react";
import { Box } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import axios from "axios";
import CustomTableWithPagination from "../../components/customTable/CustomTable";
import MemberCreditsDetailsPage from "./MemberCreditsDetailsPage";
import { getFormattedDate } from "../../Utils/commonFunction";

interface MemberCreditsTabProps {
  userId: string;
  refreshSignal?: number;
}

const apiEnpoint: any = process.env.REACT_APP_API_BASE_URL;

const MemberCreditsTab = ({ userId, refreshSignal }: MemberCreditsTabProps) => {
  const [loading, setLoading] = useState(false);
  const [credits, setCredits] = useState<any[]>([]);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedMerchant, setSelectedMerchant] = useState<any>(null);

  const fetchCredits = useCallback(() => {
    if (!userId) return;
    setLoading(true);
    const token = localStorage.getItem("businessAccessToken");
    const currentBusiness: any = localStorage?.getItem("currentBusiness");
    const business = currentBusiness ? JSON.parse(currentBusiness) : null;
    axios
      .get(
        `${apiEnpoint}/loyaltyprogram/businessportal/creditsummary/user/${userId}/business/${business?.BusinessId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          }
        }
      )
      .then((res) => {
        // API now returns direct array instead of { success, data }
        setCredits(Array.isArray(res?.data) ? res.data : []);
      })
      .catch(() => {
        setCredits([]);
      })
      .finally(() => setLoading(false));
  }, [userId]);

  useEffect(() => {
    fetchCredits();
  }, [userId, fetchCredits, refreshSignal]);

  const handleShowDetails = (merchant: any) => {
    setSelectedMerchant(merchant);
    setDetailsDialogOpen(true);
  };

  const handleCloseDetails = () => {
    setDetailsDialogOpen(false);
    setSelectedMerchant(null);
  };

  // Define columns for the credits table
  const columns = [
    {
      id: "EstablishmentName",
      label: "Establishment Name",
      minWidth: 200,
      align: "left",
      format: (value: any) => value ?? "-"
    },
    {
      id: "TotalCredits",
      label: "Total Credits",
      minWidth: 200,
      align: "left",
      format: (value: any) => (value ? `$${value}` : "-")
    },
    {
      id: "LastCreditCreated",
      label: "Last Credit Created",
      minWidth: 200,
      align: "left",
      format: (value: any) => (value ? getFormattedDate(value) : "-")
    }
  ];

  return (
    <Box>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center"
        }}
      >
        <div
          style={{
            display: "flex",
            gap: 10,
            cursor: "pointer",
            alignItems: "center",
            justifyContent: "flex-end",
            width: "100%"
          }}
        >
          <RefreshIcon onClick={() => fetchCredits()} />
        </div>
      </div>

      <CustomTableWithPagination
        columns={columns}
        data={credits || []}
        apiStatus={loading ? "fetching" : "idle"}
        isShowPagination={false}
        noDataFoundMessage="No data found."
        handleRowClick={handleShowDetails}
        sx={{ cursor: "pointer" }}
      />

      {selectedMerchant && (
        <MemberCreditsDetailsPage
          open={detailsDialogOpen}
          onClose={handleCloseDetails}
          userId={userId}
          merchantId={selectedMerchant.MerchantId}
          establishmentName={selectedMerchant.EstablishmentName}
        />
      )}
    </Box>
  );
};

export default MemberCreditsTab;

